/**
 * Authenticated app shell (OneInvoice-style):
 *
 *  ┌──────────┬─────────────────────────────────────────────┐
 *  │ Sidebar  │ TopBar: context chips · user menu           │
 *  │ (groups) ├─────────────────────────────────────────────┤
 *  │          │ TabStrip: Dashboard | Users × | Courses ×   │
 *  │          ├─────────────────────────────────────────────┤
 *  │          │ page content (scrolls)                      │
 *  └──────────┴─────────────────────────────────────────────┘
 *
 * Every authenticated page wraps itself in <DashboardLayout>; the shell state
 * (sidebar collapsed, open tabs) lives above the router so it survives navigation.
 */
import React, { useState, useEffect } from 'react';
import Sidebar from './shell/Sidebar';
import TopBar from './shell/TopBar';
import { useAuth } from '../context/AuthContext';
import { useLocation } from 'react-router-dom';
import { UpdateBanner } from './pwa/InstallApp';
import TabStrip from './shell/TabStrip';
import { ExplorerProvider } from './shell/ExplorerContext';

const COLLAPSE_KEY = 'lms_sidebar_collapsed';

export const DashboardLayout = ({ children }) => {
  const { user } = useAuth();
  const isSuper = user?.role === 'super_admin';
  const [collapsed, setCollapsed] = useState(() => {
    try { return localStorage.getItem(COLLAPSE_KEY) === '1'; } catch { return false; }
  });
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    try { localStorage.setItem(COLLAPSE_KEY, collapsed ? '1' : '0'); } catch { /* ignore */ }
  }, [collapsed]);

  // On a phone the menu is a drawer over the page. Tapping an item used to leave it
  // sitting on top of the screen it had just opened, so close it on every move.
  const location = useLocation();
  useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  const toggle = () => {
    if (window.innerWidth < 1024) setMobileOpen((o) => !o);
    else setCollapsed((c) => !c);
  };

  return (
    <ExplorerProvider>
    <div className="shell h-screen [.preview-frame_&]:h-full flex bg-shell-canvas text-foreground overflow-hidden">
      {/* Sidebar — static on desktop, drawer on mobile */}
      <div className="hidden lg:flex h-full flex-shrink-0">
        <Sidebar collapsed={collapsed} onToggle={toggle} />
      </div>
      {mobileOpen && (
        <div className="lg:hidden fixed inset-0 z-50 flex">
          <div className="h-full shadow-2xl"><Sidebar collapsed={false} onToggle={toggle} /></div>
          <button type="button" aria-label="Close menu" className="flex-1 bg-black/40" onClick={() => setMobileOpen(false)} />
        </div>
      )}

      <div className="flex-1 min-w-0 flex flex-col h-full">
        <UpdateBanner />
        {/* The navy context bar tells you which tenant you are in — which only a super
            admin needs, because everyone else belongs to exactly one organisation. For
            them the tab row carries the menu, install and account controls instead. */}
        {isSuper && <TopBar onToggleSidebar={toggle} />}
        <TabStrip onToggleSidebar={toggle} showChrome={!isSuper} />
        {/* Nothing below `lg` is read by dragging the page sideways: anything that really
            needs the width (a wide table, a code block) scrolls inside its own box. */}
        <main className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden lg:overflow-x-auto">
          {children}
        </main>
      </div>
    </div>
    </ExplorerProvider>
  );
};

export default DashboardLayout;
